import { PayloadAction } from '@reduxjs/toolkit';
import app from 'Cloud/Application/app';
import config from 'Cloud/config';
import escapeForHtml from 'lib/escapeForHtml';
import { IS_REACT_PAGE } from 'reactApp/appHelpers/configHelpers';
import { publishHelper } from 'reactApp/appHelpers/publishHelper';
import { renderNewFolderModal } from 'reactApp/components/NewFolderModal/NewFolderModal.helpers';
import { changeHomeHistory } from 'reactApp/modules/home/home.actions';
import { getHomeItemById } from 'reactApp/modules/home/home.selectors';
import { createFolderSuccess } from 'reactApp/modules/modifying/modifying.actions';
import { addFolderHelper } from 'reactApp/modules/modifying/modifying.helpers';
import { EModifyReason, ICreateFolderAction, ICreateFolderSuccess } from 'reactApp/modules/modifying/modifying.types';
import { closePopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { store as reduxStore } from 'reactApp/store';
import { openDisabledFeaturePopupHelper } from 'reactApp/ui/DisabledFeatureDialog/DisabledFeatureDialog.helpers';
import { DisabledFeature } from 'reactApp/ui/DisabledFeatureDialog/DisabledFeatureDialog.types';
import { sendGa } from 'reactApp/utils/ga';
import { noop, noopVoid } from 'reactApp/utils/helpers';
import opener from 'reactApp/utils/opener';
import { put, select } from 'redux-saga/effects';

import { createFolderErrors } from '../modifying.constants';

const MAX_NAME_LENGTH = config.get('ITEM_NAME_MAX_LENGTH');

function createFolderRequest({
    folderName,
    parent,
    skipXhr = undefined,
    conflictMode = undefined,
    storage,
    share,
    gaPrefix,
}): Promise<{ folderName: string }> {
    return new Promise((resolve, reject) => {
        folderName = folderName.trim();

        if (!folderName) {
            return reject(createFolderErrors.getMessage('required'));
        }

        if (folderName.length > MAX_NAME_LENGTH) {
            return reject(createFolderErrors.getMessage('name_too_long'));
        }

        if (app.addFolder) {
            app.addFolder(folderName, parent, skipXhr, conflictMode, storage)
                .done((newFolderName) => {
                    reduxStore.dispatch(
                        createFolderSuccess({
                            storage,
                            share,
                            folderName: newFolderName,
                            gaPrefix,
                        })
                    );
                    resolve({ folderName: newFolderName });
                })
                .fail((error) =>
                    reject(
                        createFolderErrors.getMessage(error, {
                            '{FOLDER_NAME}': escapeForHtml(folderName),
                        })
                    )
                );
        } else {
            addFolderHelper({
                item: folderName,
                parent,
                storage,
                skipXhr,
                conflictMode,
                reason: EModifyReason.add,
            })
                .then((item: any) => {
                    reduxStore.dispatch(
                        createFolderSuccess({
                            storage,
                            share,
                            folderName: item.id,
                            gaPrefix,
                        })
                    );
                    resolve({ folderName: item.id });
                })
                .catch((error) => {
                    reject(
                        createFolderErrors.getMessage((error?.home && error?.home.error) || error, {
                            '{FOLDER_NAME}': escapeForHtml(folderName),
                        })
                    );
                });
        }
    });
}

export function* createNewFolder(action: ICreateFolderAction) {
    const { parentFolderId, showCreateButton, showShareButton = true, storage, gaPrefix, onCreateFolder, onClose = noopVoid } = action;

    const isCorpUser = yield select(UserSelectors.isCorpUser);
    const { isFull: isOverQuota } = yield select(UserSelectors.getCloudSpace);

    if (isOverQuota && !isCorpUser) {
        openDisabledFeaturePopupHelper({ disabledFeature: DisabledFeature.newFolder });
        return;
    }

    yield renderNewFolderModal(
        {
            createFolder: async (folderName, share) => {
                const { folderName: newFolder } = await createFolderRequest({
                    folderName,
                    share,
                    parent: parentFolderId,
                    storage,
                    gaPrefix,
                });

                if (newFolder) {
                    onCreateFolder?.(newFolder);
                }
            },
            showShareButton,
            showCreateButton,
        },
        onClose
    );
}

export function* handleCreateNewFolder(action: PayloadAction<ICreateFolderAction>) {
    yield createNewFolder(action.payload);
}

export function* handleCreateFolderSuccess(action: PayloadAction<ICreateFolderSuccess>) {
    const { folderName, share, storage = '', gaPrefix } = action.payload;
    const { isHome } = getStorage(storage);

    yield closePopupHelper(popupNames.CREATE_FOLDER);

    const folder = IS_REACT_PAGE ? yield select(getHomeItemById, folderName) : app.get(folderName, storage);

    if (!folder) {
        return;
    }

    if (IS_REACT_PAGE) {
        if (isHome) {
            yield put(changeHomeHistory({ id: folder?.id }));
        }

        if (share && folder && folder.id !== '/') {
            publishHelper({
                item: folder,
                itemStorage: EStorageType.home,
                onClose: () => (isHome ? noop : opener(`/home/${encodeURIComponent(folder?.id)}`, true)),
            });
        }
    } else {
        sendGa('folderCreate', gaPrefix || 'home');

        let publishItem = '';
        if (share && folderName !== '/') {
            publishItem = '?action=publish-item';
        }

        opener(`/home${encodeURIComponent(folder.home)}${publishItem}`, true);
    }
}
